import React from 'react'
import { useDrop } from 'react-dnd'
import { scaleLinear as d3ScaleLinear } from 'd3'
import classnames from 'classnames'
import { GroundPlanContext } from 'src/contexts'
import { useHandler } from 'src/hooks'
import { getPointObjectType } from 'src/services'
import { PlanZoomObserver } from 'src/observers'
import styles from './Points.scss'
import Point from './Point'

const initialScale = d3ScaleLinear()
  .domain([0, 1])
  .clamp(true)

const Points = () => {
  /**
   * Retrieve plan props
   */

  const { height, width, planX, planY, background, points, dndTargets, disabled, onPointAdd } = GroundPlanContext.use()

  /**
   * Initialize local states
   */

  const [scales, setScales] = React.useState({ x: initialScale, y: initialScale })
  const [offsets, setOffsets] = React.useState({ x: 0, y: 0 })
  const [sortedPoints, setSortedPoints] = React.useState(points)

  /**
   * Sort points on hover and when new points received
   */

  // puts hovered point in last position
  const onPointOver = useHandler(point => setSortedPoints([...sortedPoints.filter(p => p !== point), point]))

  React.useEffect(() => {
    setSortedPoints(points)
  }, [points])

  const onDimensionsChange = useHandler(() => {
    const { x, y, k } = PlanZoomObserver.value
    const backgroundRatio = background.width / background.height
    const ratio = width / height // ratio of background area
    // compute width and height of image after it is shrinked by the browser to fit in the background area, when no zoom is applied.
    const [shrinkedWidth, shrinkedHeight] =
      backgroundRatio < ratio ? [backgroundRatio * height, height] : [width, width / backgroundRatio]

    setScales({
      x: scales.x.copy().range([0, shrinkedWidth * k]),
      y: scales.y.copy().range([0, shrinkedHeight * k]),
    })

    setOffsets({
      x: ((width - shrinkedWidth) / 2) * k + x,
      y: ((height - shrinkedHeight) / 2) * k + y,
    })
  })

  /**
   * Update scales and offsets when new zoom transform or dimensions change
   */
  PlanZoomObserver.useSubscribe(onDimensionsChange)
  React.useEffect(onDimensionsChange, [background, height, width])

  /**
   * Prepare drop target
   */
  const [[isOver, canDrop], drop] = useDrop({
    accept: dndTargets,
    drop: ({ data, type }, monitor) => {
      if (onPointAdd) {
        const { x: mouseX, y: mouseY } = monitor.getClientOffset()
        const { x, y } = {
          x: mouseX - planX - offsets.x,
          y: mouseY - planY - offsets.y,
        }
        onPointAdd({
          [type]: data,
          coordinates: { x: scales.x.invert(x), y: scales.y.invert(y) },
        })
      }
    },
    collect: monitor => [!!monitor.isOver(), dndTargets && !disabled && !!monitor.canDrop()],
  })

  return (
    <>
      <defs>
        <filter id="pointShadow">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="black" floodOpacity="0.3" />
        </filter>
      </defs>
      <g filter="url(#pointShadow)" transform={`translate(${offsets.x},${offsets.y})`}>
        {canDrop && (
          <rect
            ref={drop}
            className={classnames(styles.dropTarget, isOver && styles.over)}
            x="0"
            y="0"
            width={scales.x.range()[1]}
            height={scales.y.range()[1]}
            rx="4"
            ry="4"
          />
        )}
        {sortedPoints.map(point => (
          <Point
            key={getPointObjectType(point) + point.id}
            point={point}
            xScale={scales.x}
            yScale={scales.y}
            onPointOver={onPointOver}
          />
        ))}
      </g>
    </>
  )
}

export default Points
